import React, { JSX, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router'
import classNames from 'classnames'
import {
  PlusIcon,
  GiftIcon,
  TrashIcon,
  PencilSquareIcon,
  EyeIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline'
import { PlayCircleIcon, StopCircleIcon } from '@heroicons/react/24/solid'
import { LoadingSpinner } from 'components/Loading'
import { NumberInput } from 'components/NumberInput'
import { ColorInput, BackgroundInput } from 'components/ColorPickers'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { ImageUpload } from 'components/ImageUpload'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { BackgroundImageInput, fitToObjectFit } from 'components/BackgroundImageInput'
import { ScaledStage, CenteredOnCanvas } from 'components/ScaledStage'
import { GIVEAWAY, elementFontStack, elementPinnedWeight } from 'lib/elementCanvas'
import {
  ChatFontKey,
  normalizeChatFontKey
} from 'lib/chatFonts'
import { FontSelect as CatalogFontSelect } from 'components/FontSelect'
import { useFontGate } from 'lib/fontLoader'
import { sortByName } from 'lib/elementOrder'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useEventDetail } from 'hooks/useEventDetail'
import { graphqlRequest } from 'lib/helpers'
import { eventSectionPath } from 'lib/paths'
import {
  ChannelBinding,
  DeletionResult,
  EventDetail,
  GiveawayDraw,
  GiveawayStatus,
  GiveawaySummary,
  GiveawayWinner,
  ImageFit
} from 'types/api'
import createGiveawayMutation from 'graphql/createGiveaway.graphql'
import updateGiveawayMutation from 'graphql/updateGiveaway.graphql'
import deleteGiveawayMutation from 'graphql/deleteGiveaway.graphql'
import startGiveawayMutation from 'graphql/startGiveaway.graphql'
import stopGiveawayMutation from 'graphql/stopGiveaway.graphql'
import disqualifyGiveawayWinnerMutation from 'graphql/disqualifyGiveawayWinner.graphql'
import exportGiveawayCsvQuery from 'graphql/exportGiveawayCsv.graphql'

const DEFAULT_ENTRY_COMMAND = '!enter'
const ENTRY_COMMAND_MIN_LEN = 2
const ENTRY_COMMAND_MAX_LEN = 24
const MIN_WINNERS = 1
const MAX_WINNERS = 50
const MIN_ALTERNATES = 0
const MAX_ALTERNATES = 50
const DEFAULT_WINNERS = 1

export const DEFAULT_GIVEAWAY_TEXT = {
  titleText: 'The Coach Giveaway',
  entrantsLabel: 'Entrants',
  openFooterText: 'Giveaway open',
  upcomingHeadline: 'Upcoming giveaway',
  upcomingCtaText: 'Win a Coach bag',
  closedHeadline: 'Giveaway closed',
  closedSubhead: 'Winners to be announced',
  closedBody: 'Thank you for entering.',
  closedFooterText: 'Stay tuned',
  endedHeadline: 'Meet the winners',
  congratsText: 'Congratulations to our winners.',
  endedFooterText: 'Giveaway complete',
  rulesLabel: 'Official rules',
  legalText: 'No purchase necessary. 18+. Void where prohibited. This is a promotion by {sponsor}. Twitch does not sponsor or endorse this promotion and is not responsible for it.'
} as const

export const defaultOpenCta = (entryCommand: string): string =>
  `Type ${entryCommand.trim().length > 0 ? entryCommand.trim() : DEFAULT_ENTRY_COMMAND} in chat to join`

const DEFAULT_TEXT_COLOR = '#111111'
const DEFAULT_BACKGROUND = '#FFFFFF'
const DEFAULT_BORDER_COLOR = '#111111'
const DEFAULT_BORDER_WIDTH = 6
const MIN_BORDER_WIDTH = 0
const MAX_BORDER_WIDTH = 24
const DEFAULT_CORNER_RADIUS = 4
const MIN_CORNER_RADIUS = 0
const MAX_CORNER_RADIUS = 48
const DEFAULT_WINNER_OUTLINE_COLOR = '#67E8F9'
const DEFAULT_ACCENT_BACKGROUND = '#111111'
const DEFAULT_ACCENT_TEXT_COLOR = '#FFFFFF'

const DEFAULT_PRIZE_IMAGE_RADIUS = 0
const MIN_PRIZE_IMAGE_RADIUS = 0
const MAX_PRIZE_IMAGE_RADIUS = 48
const DEFAULT_PRIZE_IMAGE_BORDER_WIDTH = 0
const MIN_PRIZE_IMAGE_BORDER_WIDTH = 0
const MAX_PRIZE_IMAGE_BORDER_WIDTH = 24
const DEFAULT_PRIZE_IMAGE_BORDER_COLOR = 'transparent'
const DEFAULT_PRIZE_IMAGE_WIDTH = GIVEAWAY.prizeImage.size
const MIN_PRIZE_IMAGE_WIDTH = 80
const MAX_PRIZE_IMAGE_WIDTH = 600
const DEFAULT_COUNT_DIVIDER_COLOR = '#111111'
const DEFAULT_BRAND_DIVIDER_COLOR = '#11111159'
const DEFAULT_DIVIDER_WIDTH = 2
const MIN_DIVIDER_WIDTH = 1
const MAX_DIVIDER_WIDTH = 12
const LEGACY_BRAND_RULE_HEIGHT = 2
const LEGACY_BRAND_RULE_OPACITY = 0.35

const SAMPLE_OPEN_ENTRANT_COUNT = 1248
const SAMPLE_WINNER_HANDLES = ['pixelqueen', 'nightowl_tv', 'coachfan99', 'streamsniper', 'lofi_lena', 'byte_bandit']

export const GIVEAWAY_STATUS_PILL: Record<GiveawayStatus, string> = {
  UPCOMING: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30',
  OPEN: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30',
  CLOSED: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  ENDED: 'bg-sky-500/15 text-sky-300 ring-sky-500/30'
}

export const GIVEAWAY_STATUS_LABEL: Record<GiveawayStatus, string> = {
  UPCOMING: 'Upcoming',
  OPEN: 'Open',
  CLOSED: 'Closed',
  ENDED: 'Ended'
}

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
const checkboxClass = 'size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'

export interface GiveawayDraft {
  name: string
  entryCommand: string
  winnerCount: number
  alternateCount: number
  excludeBroadcaster: boolean
  excludeModerators: boolean
  rulesUrl: string
  sponsorName: string
  prizeName: string
  prizeImageUrl: string
  brandLogoUrl: string
  brandText: string
  backgroundImage: string
  backgroundImageFit: ImageFit
  titleText: string
  openCtaText: string
  entrantsLabel: string
  openFooterText: string
  upcomingHeadline: string
  upcomingCtaText: string
  closedHeadline: string
  closedSubhead: string
  closedBody: string
  closedFooterText: string
  endedHeadline: string
  congratsText: string
  endedFooterText: string
  rulesLabel: string
  legalText: string
  titleFontKey: ChatFontKey
  textFontKey: ChatFontKey
  captionFontKey: ChatFontKey
  subtitleFontKey: ChatFontKey
  textColor: string
  background: string
  borderColor: string
  borderWidth: number
  cornerRadius: number
  winnerOutlineColor: string
  accentBackground: string
  accentTextColor: string
  prizeImageRadius: number
  prizeImageBorderWidth: number
  prizeImageBorderColor: string
  prizeImageWidthPx: number
  countDividerEnabled: boolean
  countDividerColor: string
  countDividerWidthPx: number
  brandDividerEnabled: boolean | null
  brandDividerColor: string
  brandDividerWidthPx: number
}

export interface GiveawayLiveData {
  entrantCount: number
  winners: Array<{ login: string, displayName: string }>
}

const isRenderableWinner = (w: GiveawayWinner): boolean =>
  w.kind === 'WINNER' && w.disqualifiedAt == null

export const giveawayLiveFromSummary = (g: GiveawaySummary): GiveawayLiveData => ({
  entrantCount: g.entrantCount,
  winners: g.winners
    .filter(isRenderableWinner)
    .slice()
    .sort((a, b) => a.rank - b.rank)
    .map((w) => ({ login: w.login, displayName: w.displayName }))
})

const imageFitOf = (raw: string | null | undefined): ImageFit =>
  raw === 'CONTAIN' || raw === 'FILL' ? raw : 'COVER'

export const draftFromGiveaway = (g: GiveawaySummary): GiveawayDraft => ({
  name: g.name,
  entryCommand: g.entryCommand,
  winnerCount: g.winnerCount,
  alternateCount: g.alternateCount,
  excludeBroadcaster: g.excludeBroadcaster,
  excludeModerators: g.excludeModerators,
  rulesUrl: g.rulesUrl,
  sponsorName: g.sponsorName,
  prizeName: g.prizeName,
  prizeImageUrl: g.prizeImageUrl ?? '',
  brandLogoUrl: g.brandLogoUrl ?? '',
  brandText: g.brandText ?? '',
  backgroundImage: g.backgroundImage ?? '',
  backgroundImageFit: imageFitOf(g.backgroundImageFit),
  titleText: g.titleText ?? '',
  openCtaText: g.openCtaText ?? '',
  entrantsLabel: g.entrantsLabel ?? '',
  openFooterText: g.openFooterText ?? '',
  upcomingHeadline: g.upcomingHeadline ?? '',
  upcomingCtaText: g.upcomingCtaText ?? '',
  closedHeadline: g.closedHeadline ?? '',
  closedSubhead: g.closedSubhead ?? '',
  closedBody: g.closedBody ?? '',
  closedFooterText: g.closedFooterText ?? '',
  endedHeadline: g.endedHeadline ?? '',
  congratsText: g.congratsText ?? '',
  endedFooterText: g.endedFooterText ?? '',
  rulesLabel: g.rulesLabel ?? '',
  legalText: g.legalText ?? DEFAULT_GIVEAWAY_TEXT.legalText,
  titleFontKey: normalizeChatFontKey(g.titleFontKey ?? g.fontKey ?? 'DEFAULT'),
  textFontKey: normalizeChatFontKey(g.textFontKey ?? g.fontKey ?? 'DEFAULT'),
  captionFontKey: normalizeChatFontKey(g.captionFontKey ?? g.fontKey ?? 'DEFAULT'),
  subtitleFontKey: normalizeChatFontKey(g.subtitleFontKey ?? g.textFontKey ?? g.fontKey ?? 'DEFAULT'),
  textColor: g.textColor ?? DEFAULT_TEXT_COLOR,
  background: g.background ?? DEFAULT_BACKGROUND,
  borderColor: g.borderColor ?? DEFAULT_BORDER_COLOR,
  borderWidth: g.borderWidth ?? DEFAULT_BORDER_WIDTH,
  cornerRadius: g.cornerRadius ?? DEFAULT_CORNER_RADIUS,
  winnerOutlineColor: g.winnerOutlineColor ?? DEFAULT_WINNER_OUTLINE_COLOR,
  accentBackground: g.accentBackground ?? DEFAULT_ACCENT_BACKGROUND,
  accentTextColor: g.accentTextColor ?? DEFAULT_ACCENT_TEXT_COLOR,
  prizeImageRadius: g.prizeImageRadius ?? DEFAULT_PRIZE_IMAGE_RADIUS,
  prizeImageBorderWidth: g.prizeImageBorderWidth ?? DEFAULT_PRIZE_IMAGE_BORDER_WIDTH,
  prizeImageBorderColor: g.prizeImageBorderColor ?? DEFAULT_PRIZE_IMAGE_BORDER_COLOR,
  prizeImageWidthPx: g.prizeImageWidthPx ?? DEFAULT_PRIZE_IMAGE_WIDTH,
  countDividerEnabled: g.countDividerEnabled ?? false,
  countDividerColor: g.countDividerColor ?? DEFAULT_COUNT_DIVIDER_COLOR,
  countDividerWidthPx: g.countDividerWidthPx ?? DEFAULT_DIVIDER_WIDTH,
  brandDividerEnabled: g.brandDividerEnabled ?? null,
  brandDividerColor: g.brandDividerColor ?? DEFAULT_BRAND_DIVIDER_COLOR,
  brandDividerWidthPx: g.brandDividerWidthPx ?? DEFAULT_DIVIDER_WIDTH
})

const emptyDraft = (): GiveawayDraft => ({
  name: '',
  entryCommand: DEFAULT_ENTRY_COMMAND,
  winnerCount: DEFAULT_WINNERS,
  alternateCount: DEFAULT_WINNERS,
  excludeBroadcaster: true,
  excludeModerators: true,
  rulesUrl: '',
  sponsorName: '',
  prizeName: '',
  prizeImageUrl: '',
  brandLogoUrl: '',
  brandText: '',
  backgroundImage: '',
  backgroundImageFit: 'COVER',
  titleText: DEFAULT_GIVEAWAY_TEXT.titleText,
  openCtaText: defaultOpenCta(DEFAULT_ENTRY_COMMAND),
  entrantsLabel: DEFAULT_GIVEAWAY_TEXT.entrantsLabel,
  openFooterText: DEFAULT_GIVEAWAY_TEXT.openFooterText,
  upcomingHeadline: DEFAULT_GIVEAWAY_TEXT.upcomingHeadline,
  upcomingCtaText: DEFAULT_GIVEAWAY_TEXT.upcomingCtaText,
  closedHeadline: DEFAULT_GIVEAWAY_TEXT.closedHeadline,
  closedSubhead: DEFAULT_GIVEAWAY_TEXT.closedSubhead,
  closedBody: DEFAULT_GIVEAWAY_TEXT.closedBody,
  closedFooterText: DEFAULT_GIVEAWAY_TEXT.closedFooterText,
  endedHeadline: DEFAULT_GIVEAWAY_TEXT.endedHeadline,
  congratsText: DEFAULT_GIVEAWAY_TEXT.congratsText,
  endedFooterText: DEFAULT_GIVEAWAY_TEXT.endedFooterText,
  rulesLabel: DEFAULT_GIVEAWAY_TEXT.rulesLabel,
  legalText: DEFAULT_GIVEAWAY_TEXT.legalText,
  titleFontKey: 'DEFAULT',
  textFontKey: 'DEFAULT',
  captionFontKey: 'DEFAULT',
  subtitleFontKey: 'DEFAULT',
  textColor: DEFAULT_TEXT_COLOR,
  background: DEFAULT_BACKGROUND,
  borderColor: DEFAULT_BORDER_COLOR,
  borderWidth: DEFAULT_BORDER_WIDTH,
  cornerRadius: DEFAULT_CORNER_RADIUS,
  winnerOutlineColor: DEFAULT_WINNER_OUTLINE_COLOR,
  accentBackground: DEFAULT_ACCENT_BACKGROUND,
  accentTextColor: DEFAULT_ACCENT_TEXT_COLOR,
  prizeImageRadius: DEFAULT_PRIZE_IMAGE_RADIUS,
  prizeImageBorderWidth: DEFAULT_PRIZE_IMAGE_BORDER_WIDTH,
  prizeImageBorderColor: DEFAULT_PRIZE_IMAGE_BORDER_COLOR,
  prizeImageWidthPx: DEFAULT_PRIZE_IMAGE_WIDTH,
  countDividerEnabled: false,
  countDividerColor: DEFAULT_COUNT_DIVIDER_COLOR,
  countDividerWidthPx: DEFAULT_DIVIDER_WIDTH,
  brandDividerEnabled: null,
  brandDividerColor: DEFAULT_BRAND_DIVIDER_COLOR,
  brandDividerWidthPx: DEFAULT_DIVIDER_WIDTH
})

const GIVEAWAY_WINNER_KEYFRAMES = `
@keyframes spGiveawayGlint { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@keyframes spGiveawayPulse { 0%, 100% { opacity: 0.62; } 50% { opacity: 1; } }
@keyframes spGiveawayFadeIn { from { opacity: 0; } to { opacity: 1; } }
`

const GLOW_INSET = 5
const GiveawayWinnerGlow = ({ color, radius }: { color: string, radius: number }): JSX.Element => (
  <div
    data-testid='giveaway-winner-glow'
    style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      opacity: 0,
      animation: 'spGiveawayFadeIn 500ms ease-out forwards'
    }}
  >
    <div
      style={{
        position: 'absolute',
        inset: `-${GLOW_INSET}px`,
        borderRadius: `${radius + GLOW_INSET}px`,
        border: `2.5px solid ${color}`,
        boxShadow: `0 0 16px ${color}, 0 0 5px ${color}, inset 0 0 12px ${color}55`,
        animation: 'spGiveawayPulse 2.2s ease-in-out infinite'
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: `-${GLOW_INSET}px`,
        borderRadius: `${radius + GLOW_INSET}px`,
        padding: '2.5px',
        background: 'linear-gradient(110deg, transparent 38%, rgba(255,255,255,0.92) 50%, transparent 62%)',
        backgroundSize: '220% 100%',
        backgroundRepeat: 'no-repeat',
        animation: 'spGiveawayGlint 2.6s linear infinite',
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude'
      }}
    />
  </div>
)

const textOr = (value: string, fallback: string): string =>
  value.trim().length > 0 ? value.trim() : fallback

const legalLineFor = (draft: GiveawayDraft): string | null => {
  const raw = draft.legalText.trim()
  if (raw.length === 0) return null
  return raw.replace(/\{sponsor\}/g, draft.sponsorName.trim().length > 0 ? draft.sponsorName.trim() : 'the host')
}

const upper: React.CSSProperties = { textTransform: 'uppercase' }

const HEADLINE_LETTER_SPACING_EM = Number.parseFloat(GIVEAWAY.headline.letterSpacing)
const MIN_HEADLINE_PX = 18
const FIT_SAFETY = 0.98

let fitCtx: CanvasRenderingContext2D | null | undefined
const fittedHeadlinePx = (text: string, fontFamily: string, fontWeight: number, availableWidth: number): number => {
  const base = GIVEAWAY.headline.fontSize
  if (availableWidth <= 0) return base
  if (fitCtx === undefined) {
    fitCtx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d')
  }
  if (fitCtx === null) return base
  const upperText = text.toUpperCase()
  fitCtx.font = `${fontWeight} ${base}px ${fontFamily}`
  const measured = fitCtx.measureText(upperText).width +
    HEADLINE_LETTER_SPACING_EM * base * Math.max(0, upperText.length - 1)
  if (measured <= availableWidth) return base
  return Math.max(MIN_HEADLINE_PX, Math.floor(base * (availableWidth / measured) * FIT_SAFETY))
}

export const GiveawayPreview = ({
  draft,
  state,
  live,
  layoutWidth = GIVEAWAY.width
}: {
  draft: GiveawayDraft
  state: GiveawayStatus
  live?: GiveawayLiveData
  layoutWidth?: number
}): JSX.Element => {
  const titleFamily = elementFontStack(draft.titleFontKey)
  const textFamily = elementFontStack(draft.textFontKey)
  const captionFamily = elementFontStack(draft.captionFontKey)
  const subtitleFamily = elementFontStack(draft.subtitleFontKey)
  const titleFontReady = useFontGate(draft.titleFontKey)
  const textFontReady = useFontGate(draft.textFontKey)
  const captionFontReady = useFontGate(draft.captionFontKey)
  const subtitleFontReady = useFontGate(draft.subtitleFontKey)
  const fontReady = titleFontReady && textFontReady && captionFontReady && subtitleFontReady
  const hasBgImage = draft.backgroundImage.trim().length > 0
  const hasPrizeImage = draft.prizeImageUrl.trim().length > 0
  const hasBrandLogo = draft.brandLogoUrl.trim().length > 0
  const brandText = draft.brandText.trim()
  const prizeName = textOr(draft.prizeName, 'Prize name')
  const rulesUrl = textOr(draft.rulesUrl, 'https://example.com/rules')
  const winners = live?.winners ?? []
  const entrantCount = live?.entrantCount ?? 0
  const ended = state === 'ENDED'
  const openImageSize = draft.prizeImageWidthPx
  const bannerImageSize = Math.round(draft.prizeImageWidthPx * GIVEAWAY.prizeImage.bannerSize / GIVEAWAY.prizeImage.size)
  const legalLine = legalLineFor(draft)
  if (!fontReady) return <CenteredOnCanvas width={layoutWidth}>{null}</CenteredOnCanvas>

  const brand = hasBrandLogo
    ? (
      <img
        src={draft.brandLogoUrl}
        alt=''
        aria-hidden='true'
        data-testid='giveaway-brand-logo'
        style={{ display: 'block', alignSelf: 'center', height: `${GIVEAWAY.brand.logoHeight}px`, maxWidth: '360px', objectFit: 'contain' }}
        onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
      />
      )
    : brandText.length > 0
      ? (
        <div
          data-testid='giveaway-brand-text'
          style={{
            ...upper,
            alignSelf: 'center',
            textAlign: 'center',
            fontSize: `${GIVEAWAY.brand.fontSize}px`,
            lineHeight: `${GIVEAWAY.brand.lineHeight}px`,
            fontWeight: GIVEAWAY.brand.fontWeight,
            letterSpacing: GIVEAWAY.brand.letterSpacing
          }}
        >
          {brandText}
        </div>
        )
      : null

  const brandRule = (): JSX.Element | null => {
    if (brand === null) return null
    if (draft.brandDividerEnabled === true) {
      return (
        <div
          aria-hidden='true'
          data-testid='giveaway-brand-divider'
          style={{ height: `${draft.brandDividerWidthPx}px`, background: draft.brandDividerColor, alignSelf: 'stretch', width: '100%' }}
        />
      )
    }
    if (draft.brandDividerEnabled === null && state === 'UPCOMING') {
      return (
        <div
          aria-hidden='true'
          data-testid='giveaway-brand-divider'
          style={{ height: `${LEGACY_BRAND_RULE_HEIGHT}px`, background: 'currentColor', opacity: LEGACY_BRAND_RULE_OPACITY, alignSelf: 'stretch', width: '100%' }}
        />
      )
    }
    return null
  }

  const prizeImage = (size: number): JSX.Element | null => hasPrizeImage
    ? (
      <img
        src={draft.prizeImageUrl}
        alt=''
        aria-hidden='true'
        data-testid='giveaway-prize-image'
        style={{
          display: 'block',
          width: `${size}px`,
          aspectRatio: '1 / 1',
          maxWidth: '100%',
          flexShrink: 0,
          objectFit: 'cover',
          borderRadius: `${draft.prizeImageRadius}px`,
          border: draft.prizeImageBorderWidth > 0 ? `${draft.prizeImageBorderWidth}px solid ${draft.prizeImageBorderColor}` : undefined
        }}
        onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
      />
      )
    : null

  const headline = (text: string, fitWidth: number): JSX.Element => {
    const headlineWeight = elementPinnedWeight(draft.titleFontKey, GIVEAWAY.headline.fontWeight)
    const px = fittedHeadlinePx(text, titleFamily, headlineWeight, fitWidth)
    return (
      <div
        data-testid='giveaway-headline'
        style={{
          ...upper,
          fontFamily: titleFamily,
          fontSize: `${px}px`,
          lineHeight: `${Math.round(px * GIVEAWAY.headline.lineHeight / GIVEAWAY.headline.fontSize)}px`,
          fontWeight: headlineWeight,
          letterSpacing: GIVEAWAY.headline.letterSpacing,
          textAlign: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </div>
    )
  }

  const accentStrip = (text: string): JSX.Element => (
    <div
      data-testid='giveaway-accent-strip'
      style={{
        ...upper,
        fontFamily: subtitleFamily,
        minHeight: `${GIVEAWAY.accentStrip.minHeight}px`,
        paddingTop: `${GIVEAWAY.accentStrip.paddingY}px`,
        paddingBottom: `${GIVEAWAY.accentStrip.paddingY}px`,
        paddingLeft: `${GIVEAWAY.padding}px`,
        paddingRight: `${GIVEAWAY.padding}px`,
        background: draft.accentBackground,
        color: draft.accentTextColor,
        fontSize: `${GIVEAWAY.accentStrip.fontSize}px`,
        lineHeight: `${GIVEAWAY.accentStrip.lineHeight}px`,
        fontWeight: elementPinnedWeight(draft.subtitleFontKey, GIVEAWAY.accentStrip.fontWeight),
        letterSpacing: GIVEAWAY.accentStrip.letterSpacing,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {text}
    </div>
  )

  const rulesLine = (
    <div
      data-testid='giveaway-rules-line'
      style={{
        paddingBottom: `${GIVEAWAY.rules.paddingBottom}px`,
        paddingLeft: `${GIVEAWAY.padding}px`,
        paddingRight: `${GIVEAWAY.padding}px`,
        textAlign: 'center',
        fontSize: `${GIVEAWAY.rules.fontSize}px`,
        lineHeight: `${GIVEAWAY.rules.lineHeight}px`,
        fontWeight: GIVEAWAY.rules.fontWeight,
        wordBreak: 'break-all'
      }}
    >
      {textOr(draft.rulesLabel, DEFAULT_GIVEAWAY_TEXT.rulesLabel)}: {rulesUrl}
    </div>
  )
  const legalBlock = legalLine === null
    ? null
    : (
      <div
        data-testid='giveaway-legal-line'
        style={{
          paddingTop: `${GIVEAWAY.legal.paddingY}px`,
          paddingBottom: `${GIVEAWAY.legal.paddingY}px`,
          paddingLeft: `${GIVEAWAY.padding}px`,
          paddingRight: `${GIVEAWAY.padding}px`,
          textAlign: 'center',
          fontSize: `${GIVEAWAY.legal.fontSize}px`,
          lineHeight: `${GIVEAWAY.legal.lineHeight}px`,
          fontWeight: GIVEAWAY.legal.fontWeight,
          opacity: 0.85
        }}
      >
        {legalLine}
      </div>
      )

  const padded: React.CSSProperties = {
    paddingTop: `${GIVEAWAY.padding}px`,
    paddingLeft: `${GIVEAWAY.padding}px`,
    paddingRight: `${GIVEAWAY.padding}px`,
    paddingBottom: `${GIVEAWAY.padding}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: `${GIVEAWAY.blockGap}px`
  }

  const upcomingFitWidth = layoutWidth - 2 * GIVEAWAY.padding - (hasPrizeImage ? bannerImageSize + GIVEAWAY.padding : 0)
  const cardFitWidth = layoutWidth - 2 * GIVEAWAY.padding

  let content: JSX.Element
  if (state === 'UPCOMING') {
    content = (
      <>
        <div style={{ ...padded, flexDirection: 'row', alignItems: 'center', gap: `${GIVEAWAY.padding}px` }}>
          {prizeImage(bannerImageSize)}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: `${GIVEAWAY.blockGap}px` }}>
            {brand}
            {brandRule()}
            {headline(textOr(draft.upcomingHeadline, DEFAULT_GIVEAWAY_TEXT.upcomingHeadline), upcomingFitWidth)}
          </div>
        </div>
        {rulesLine}
        {accentStrip(textOr(draft.upcomingCtaText, DEFAULT_GIVEAWAY_TEXT.upcomingCtaText))}
        {legalBlock}
      </>
    )
  } else if (state === 'OPEN') {
    content = (
      <>
        <div style={padded}>
          {brand}
          {brandRule()}
          {headline(textOr(draft.titleText, DEFAULT_GIVEAWAY_TEXT.titleText), cardFitWidth)}
          <div
            data-testid='giveaway-open-cta'
            style={{
              textAlign: 'center',
              fontSize: `${GIVEAWAY.cta.fontSize}px`,
              lineHeight: `${GIVEAWAY.cta.lineHeight}px`,
              fontWeight: GIVEAWAY.cta.fontWeight,
              opacity: 0.92
            }}
          >
            {textOr(draft.openCtaText, defaultOpenCta(draft.entryCommand))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: `${GIVEAWAY.padding}px` }}>
            {hasPrizeImage && (
              <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                {prizeImage(openImageSize)}
              </div>
            )}
            {draft.countDividerEnabled && hasPrizeImage && (
              <div
                aria-hidden='true'
                data-testid='giveaway-count-divider'
                style={{ width: `${draft.countDividerWidthPx}px`, alignSelf: 'stretch', flexShrink: 0, background: draft.countDividerColor }}
              />
            )}
            <div style={{ flex: '1 1 0', minWidth: 0, textAlign: 'center' }}>
              <div
                data-testid='giveaway-entrant-count'
                style={{
                  fontFamily: titleFamily,
                  fontSize: `${GIVEAWAY.count.fontSize}px`,
                  lineHeight: `${GIVEAWAY.count.lineHeight}px`,
                  fontWeight: elementPinnedWeight(draft.titleFontKey, GIVEAWAY.count.fontWeight),
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {entrantCount.toLocaleString()}
              </div>
              <div
                data-testid='giveaway-entrants-label'
                style={{
                  ...upper,
                  marginTop: '8px',
                  fontSize: `${GIVEAWAY.countLabel.fontSize}px`,
                  lineHeight: `${GIVEAWAY.countLabel.lineHeight}px`,
                  fontWeight: GIVEAWAY.countLabel.fontWeight,
                  letterSpacing: GIVEAWAY.countLabel.letterSpacing
                }}
              >
                {textOr(draft.entrantsLabel, DEFAULT_GIVEAWAY_TEXT.entrantsLabel)}
              </div>
            </div>
          </div>
        </div>
        {rulesLine}
        {accentStrip(textOr(draft.openFooterText, DEFAULT_GIVEAWAY_TEXT.openFooterText))}
        {legalBlock}
      </>
    )
  } else if (state === 'CLOSED') {
    content = (
      <>
        <div style={{ ...padded, textAlign: 'center', alignItems: 'center' }}>
          {brand}
          {brandRule()}
          {headline(textOr(draft.closedHeadline, DEFAULT_GIVEAWAY_TEXT.closedHeadline), cardFitWidth)}
          <div
            data-testid='giveaway-subhead'
            style={{
              ...upper,
              fontSize: `${GIVEAWAY.subhead.fontSize}px`,
              lineHeight: `${GIVEAWAY.subhead.lineHeight}px`,
              fontWeight: GIVEAWAY.subhead.fontWeight,
              letterSpacing: GIVEAWAY.subhead.letterSpacing
            }}
          >
            {textOr(draft.closedSubhead, DEFAULT_GIVEAWAY_TEXT.closedSubhead)}
          </div>
          <div
            data-testid='giveaway-body'
            style={{
              fontFamily: captionFamily,
              fontSize: `${GIVEAWAY.body.fontSize}px`,
              lineHeight: `${GIVEAWAY.body.lineHeight}px`,
              fontWeight: GIVEAWAY.body.fontWeight,
              opacity: 0.92
            }}
          >
            {textOr(draft.closedBody, DEFAULT_GIVEAWAY_TEXT.closedBody)}
          </div>
        </div>
        {rulesLine}
        {accentStrip(textOr(draft.closedFooterText, DEFAULT_GIVEAWAY_TEXT.closedFooterText))}
        {legalBlock}
      </>
    )
  } else {
    content = (
      <>
        <div style={{ ...padded, textAlign: 'center', alignItems: 'center' }}>
          {brand}
          {brandRule()}
          {headline(textOr(draft.endedHeadline, DEFAULT_GIVEAWAY_TEXT.endedHeadline), cardFitWidth)}
          <div
            data-testid='giveaway-prize-name'
            style={{
              ...upper,
              fontSize: `${GIVEAWAY.subhead.fontSize}px`,
              lineHeight: `${GIVEAWAY.subhead.lineHeight}px`,
              fontWeight: GIVEAWAY.subhead.fontWeight,
              letterSpacing: GIVEAWAY.subhead.letterSpacing
            }}
          >
            {`${prizeName} Giveaway`}
          </div>
          <div
            data-testid='giveaway-winner-grid'
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: `${GIVEAWAY.winnerBox.gap}px`,
              margin: `${GLOW_INSET}px 0`,
              width: '100%'
            }}
          >
            {winners.map((w) => (
              <div
                key={w.login}
                data-testid='giveaway-winner-box'
                style={{
                  ...upper,
                  fontFamily: titleFamily,
                  position: 'relative',
                  minWidth: `${GIVEAWAY.winnerBox.minWidth}px`,
                  paddingTop: `${GIVEAWAY.winnerBox.paddingY}px`,
                  paddingBottom: `${GIVEAWAY.winnerBox.paddingY}px`,
                  paddingLeft: `${GIVEAWAY.winnerBox.paddingX}px`,
                  paddingRight: `${GIVEAWAY.winnerBox.paddingX}px`,
                  borderRadius: `${GIVEAWAY.winnerBox.radius}px`,
                  background: draft.accentBackground,
                  color: draft.accentTextColor,
                  fontSize: `${GIVEAWAY.winnerBox.fontSize}px`,
                  lineHeight: `${GIVEAWAY.winnerBox.lineHeight}px`,
                  fontWeight: elementPinnedWeight(draft.titleFontKey, GIVEAWAY.winnerBox.fontWeight),
                  letterSpacing: GIVEAWAY.winnerBox.letterSpacing,
                  textAlign: 'center',
                  wordBreak: 'break-all'
                }}
              >
                <GiveawayWinnerGlow color={draft.winnerOutlineColor} radius={GIVEAWAY.winnerBox.radius} />
                @{w.displayName}
              </div>
            ))}
          </div>
          <div
            data-testid='giveaway-congrats'
            style={{
              fontFamily: captionFamily,
              fontSize: `${GIVEAWAY.body.fontSize}px`,
              lineHeight: `${GIVEAWAY.body.lineHeight}px`,
              fontWeight: GIVEAWAY.body.fontWeight,
              opacity: 0.92
            }}
          >
            {textOr(draft.congratsText, DEFAULT_GIVEAWAY_TEXT.congratsText)}
          </div>
        </div>
        {rulesLine}
        {accentStrip(textOr(draft.endedFooterText, DEFAULT_GIVEAWAY_TEXT.endedFooterText))}
        {legalBlock}
      </>
    )
  }

  return (
    <CenteredOnCanvas width={layoutWidth}>
      <div
        data-testid='giveaway-preview'
        data-state={state}
        style={{
          position: 'relative',
          width: '100%',
          fontFamily: textFamily,
          color: draft.textColor,
          overflow: 'hidden',
          borderRadius: `${draft.cornerRadius}px`,
          border: `${draft.borderWidth}px solid ${draft.borderColor}`,
          background: draft.background
        }}
      >
        {hasBgImage && (
          <img
            src={draft.backgroundImage}
            alt=''
            aria-hidden='true'
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: fitToObjectFit(draft.backgroundImageFit)
            }}
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
          />
        )}
        {ended && <style>{GIVEAWAY_WINNER_KEYFRAMES}</style>}
        <div style={{ position: 'relative' }}>
          {content}
        </div>
      </div>
    </CenteredOnCanvas>
  )
}

export const sampleWinnersLive = (winnerCount: number): GiveawayLiveData => ({
  entrantCount: SAMPLE_OPEN_ENTRANT_COUNT,
  winners: Array.from({ length: Math.max(1, Math.min(winnerCount, MAX_WINNERS)) }, (_, i) => {
    const handle = SAMPLE_WINNER_HANDLES[i % SAMPLE_WINNER_HANDLES.length]
    const login = i < SAMPLE_WINNER_HANDLES.length ? handle : `${handle}${i}`
    return { login, displayName: login }
  })
})

const GiveawayPreviewBody = ({ draft, existing, readOnly }: {
  draft: GiveawayDraft
  existing: GiveawaySummary | null
  readOnly: boolean
}): JSX.Element => {
  if (readOnly && existing !== null) {
    return (
      <div>
        <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Last state
        </div>
        <ScaledStage mode='preview' showCheckerboard elementWidth={GIVEAWAY.width}>
          <GiveawayPreview draft={draft} state={existing.status} live={giveawayLiveFromSummary(existing)} />
        </ScaledStage>
      </div>
    )
  }
  const stages: Array<{ label: string, state: GiveawayStatus, live?: GiveawayLiveData }> = [
    { label: 'Upcoming', state: 'UPCOMING' },
    { label: 'Open (entries counting)', state: 'OPEN', live: { entrantCount: SAMPLE_OPEN_ENTRANT_COUNT, winners: [] } },
    { label: 'Closed (awaiting the draw)', state: 'CLOSED', live: { entrantCount: SAMPLE_OPEN_ENTRANT_COUNT, winners: [] } },
    { label: 'Winners published', state: 'ENDED', live: sampleWinnersLive(draft.winnerCount) }
  ]
  return (
    <div className='space-y-3'>
      {stages.map((s) => (
        <div key={s.state} data-testid={`giveaway-stage-${s.state.toLowerCase()}`}>
          <div className='mb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            {s.label}
          </div>
          <ScaledStage mode='preview' showCheckerboard elementWidth={GIVEAWAY.width}>
            <GiveawayPreview draft={draft} state={s.state} live={s.live} />
          </ScaledStage>
        </div>
      ))}
    </div>
  )
}

const drawLoginById = (draw: GiveawayDraw): Map<string, string> => {
  const map = new Map<string, string>()
  for (const w of [...draw.winners, ...draw.alternates]) {
    if (w.twitchUserId != null) map.set(w.twitchUserId, w.login)
  }
  return map
}

const drawnCounts = (draw: GiveawayDraw): { winners: number, alternates: number } => {
  const winners = Math.min(draw.eligibleCount, draw.requestedWinnerCount)
  const alternates = Math.max(0, Math.min(draw.eligibleCount, draw.requestedWinnerCount + draw.requestedAlternateCount) - draw.requestedWinnerCount)
  return { winners, alternates }
}

const csvFileName = (giveawayId: string): string =>
  `giveaway-${giveawayId}-${new Date().toISOString().slice(0, 10)}.csv`

const downloadCsv = (giveawayId: string, csv: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = globalThis.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = csvFileName(giveawayId)
  a.setAttribute('data-testid', 'giveaway-csv-anchor')
  document.body.appendChild(a)
  a.click()
  a.remove()
  globalThis.URL.revokeObjectURL(url)
}

export const GiveawayWinnersTable = ({
  draw,
  canDisqualify,
  busyId,
  disabled,
  onDisqualify
}: {
  draw: GiveawayDraw
  canDisqualify: boolean
  busyId: string | null
  disabled: boolean
  onDisqualify: (w: GiveawayWinner) => void
}): JSX.Element => {
  const rows: GiveawayWinner[] = [...draw.winners, ...draw.alternates]
  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-left text-xs' data-testid='giveaway-draw-table'>
        <thead>
          <tr className='text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            <th className='py-1 pr-3'>Rank</th>
            <th className='py-1 pr-3'>Kind</th>
            <th className='py-1 pr-3'>Twitch</th>
            <th className='py-1 pr-3'>Disqualified</th>
            <th className='py-1' />
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => {
            const dq = w.disqualifiedAt != null
            const id = w.twitchUserId ?? ''
            return (
              <tr key={`${w.kind}:${w.rank}:${w.login}`} className={classNames('border-t border-white/5', dq && 'opacity-60')} data-testid={`giveaway-draw-row-${w.kind.toLowerCase()}-${w.rank}`}>
                <td className='py-1.5 pr-3 font-mono text-dm-text-primary'>{w.rank}</td>
                <td className='py-1.5 pr-3 text-dm-text-secondary'>{w.kind === 'WINNER' ? 'Winner' : 'Alternate'}</td>
                <td className='py-1.5 pr-3 text-dm-text-primary'>
                  {w.displayName}{w.displayName.toLowerCase() !== w.login.toLowerCase() ? ` (@${w.login})` : ''}
                </td>
                <td className='py-1.5 pr-3 text-dm-text-secondary' title={dq ? `${w.disqualifiedAt ?? ''}${(w.disqualifiedReason ?? '').length > 0 ? ` — ${w.disqualifiedReason ?? ''}` : ''}` : undefined}>
                  {dq ? 'Disqualified' : '—'}
                </td>
                <td className='py-1.5 text-right'>
                  {canDisqualify && w.kind === 'WINNER' && !dq && id.length > 0 && (
                    <button
                      type='button'
                      onClick={() => onDisqualify(w)}
                      disabled={disabled}
                      data-testid={`giveaway-disqualify-${w.login}`}
                      className='inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-dm-text-secondary hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
                    >
                      {busyId === id ? <LoadingSpinner /> : <NoSymbolIcon className='size-3' aria-hidden='true' />}
                      Disqualify
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export const promptDisqualifyReason = (w: GiveawayWinner): string | null =>
  globalThis.prompt(
    `Disqualify @${w.login} (${w.displayName})?\n\nThe next alternate is promoted in draw order and this disqualification (who / when / reason) is recorded in the draw record. Enter the reason:`
  )

const DrawSection = ({
  giveaway,
  draw,
  readOnly,
  onUpserted
}: {
  giveaway: GiveawaySummary
  draw: GiveawayDraw
  readOnly: boolean
  onUpserted: (giveaway: GiveawaySummary) => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const canDisqualify = giveaway.status !== 'ENDED'

  const handleExport = async (): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    setBusy('export')
    setError(null)
    try {
      const { csv } = await graphqlRequest<{ csv: string | null }>(
        exportGiveawayCsvQuery, { giveawayId: giveaway.giveawayId }, auth0Token
      )
      if (csv === null || csv.length === 0) throw new Error('The export came back empty')
      downloadCsv(giveaway.giveawayId, csv)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export the CSV')
    } finally {
      setBusy(null)
    }
  }

  const handleDisqualify = async (w: GiveawayWinner): Promise<void> => {
    if (auth0Token === undefined || busy !== null || !canDisqualify) return
    if (w.twitchUserId == null) return
    const reason = promptDisqualifyReason(w)
    if (reason === null) return
    if (reason.trim().length === 0) {
      setError('A reason is required to disqualify a winner.')
      return
    }
    setBusy(`dq:${w.twitchUserId}`)
    setError(null)
    try {
      const { giveaway: updated } = await graphqlRequest<{ giveaway: Partial<GiveawaySummary> & { giveawayId: string } }>(
        disqualifyGiveawayWinnerMutation,
        { giveawayId: giveaway.giveawayId, twitchUserId: w.twitchUserId, reason: reason.trim() },
        auth0Token
      )
      onUpserted({ ...giveaway, ...updated })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disqualify the winner')
    } finally {
      setBusy(null)
    }
  }

  const drawn = drawnCounts(draw)
  const loginById = drawLoginById(draw)
  const handleLabel = (twitchUserId: string | null): string => {
    const login = twitchUserId === null ? undefined : loginById.get(twitchUserId)
    return login === undefined ? 'a removed entrant' : `@${login}`
  }

  return (
    <SectionCard
      title='Draw record'
      action={
        <button
          type='button'
          onClick={() => { void handleExport() }}
          disabled={busy !== null}
          data-testid='giveaway-export-csv'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          {busy === 'export' ? <LoadingSpinner /> : <ArrowDownTrayIcon className='size-3.5' aria-hidden='true' />}
          Export CSV
        </button>
      }
    >
      {error !== null && (
        <div className='mb-3'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
      {draw.shortPool && (
        <div className='mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200' data-testid='giveaway-draw-short-pool'>
          Fewer eligible entrants than requested: drew {drawn.winners} of {draw.requestedWinnerCount} winners and {drawn.alternates} of {draw.requestedAlternateCount} alternates.
        </div>
      )}
      <dl className='mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:grid-cols-3' data-testid='giveaway-draw-summary'>
        <div>
          <dt className='text-dm-text-accent'>Method</dt>
          <dd className='text-dm-text-primary'>
            <span className='font-mono'>{draw.methodVersion}</span>
            <span
              data-testid='giveaway-draw-method-indicator'
              className={classNames(
                'ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset',
                draw.fallbackUsed ? 'bg-amber-500/15 text-amber-300 ring-amber-500/30' : 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
              )}
            >
              {draw.fallbackUsed ? 'KMS fallback' : 'drand beacon'}
            </span>
          </dd>
        </div>
        <div><dt className='text-dm-text-accent'>drand round</dt><dd className='font-mono text-dm-text-primary'>{draw.targetRound}</dd></div>
        <div><dt className='text-dm-text-accent'>Drawn at</dt><dd className='text-dm-text-primary'>{draw.drawnAt}</dd></div>
      </dl>
      <GiveawayWinnersTable
        draw={draw}
        canDisqualify={canDisqualify}
        busyId={busy?.startsWith('dq:') === true ? busy.slice(3) : null}
        disabled={busy !== null}
        onDisqualify={(w) => { void handleDisqualify(w) }}
      />
      {draw.disqualifications.length > 0 && (
        <ul className='mt-3 space-y-1 text-[11px] text-dm-text-accent' data-testid='giveaway-disqualifications'>
          {draw.disqualifications.map((d) => (
            <li key={`${d.twitchUserId}:${d.at}`}>
              {d.at} — {handleLabel(d.twitchUserId)} disqualified: {d.reason}
              {d.promotedTwitchUserId !== null ? ` → promoted ${handleLabel(d.promotedTwitchUserId)}` : ''}
            </li>
          ))}
        </ul>
      )}
      <p className='mt-3 text-[11px] leading-relaxed text-dm-text-accent'>
        Anyone can re-run this draw from the CSV: the frozen entrant ids, the draw id, the method
        version and the drand round reproduce the same winners. Export within 90 days of close for
        the full entrant list entrant rows expire; the draw record stays with the giveaway.
      </p>
    </SectionCard>
  )
}

interface TextSlotRow { key: keyof GiveawayDraft & string, label: string, hint: string, where: string }

const TEXT_GROUPS: Array<{ label: string, note?: string, slots: TextSlotRow[] }> = [
  {
    label: 'Upcoming state',
    slots: [
      { key: 'upcomingHeadline', label: 'Headline', hint: DEFAULT_GIVEAWAY_TEXT.upcomingHeadline, where: 'The big one-line banner headline ("UPCOMING GIVEAWAY")' },
      { key: 'upcomingCtaText', label: 'Strip line', hint: DEFAULT_GIVEAWAY_TEXT.upcomingCtaText, where: 'The accent strip under the banner ("WIN A COACH BAG")' }
    ]
  },
  {
    label: 'Open state',
    slots: [
      { key: 'titleText', label: 'Title', hint: DEFAULT_GIVEAWAY_TEXT.titleText, where: 'The big headline at the top of the open card ("THE COACH GIVEAWAY")' },
      { key: 'openCtaText', label: 'Call to action', hint: defaultOpenCta(DEFAULT_ENTRY_COMMAND), where: 'Under the title — follows the entry command until you edit it' },
      { key: 'entrantsLabel', label: 'Entrants label', hint: DEFAULT_GIVEAWAY_TEXT.entrantsLabel, where: 'The word under the live count ("ENTRANTS")' },
      { key: 'openFooterText', label: 'Strip line', hint: DEFAULT_GIVEAWAY_TEXT.openFooterText, where: 'The accent strip at the bottom ("GIVEAWAY OPEN")' }
    ]
  },
  {
    label: 'Closed state',
    slots: [
      { key: 'closedHeadline', label: 'Headline', hint: DEFAULT_GIVEAWAY_TEXT.closedHeadline, where: 'The big headline ("GIVEAWAY CLOSED")' },
      { key: 'closedSubhead', label: 'Subhead', hint: DEFAULT_GIVEAWAY_TEXT.closedSubhead, where: 'Under the headline ("WINNERS TO BE ANNOUNCED")' },
      { key: 'closedBody', label: 'Body line', hint: DEFAULT_GIVEAWAY_TEXT.closedBody, where: 'The sentence under the subhead' },
      { key: 'closedFooterText', label: 'Strip line', hint: DEFAULT_GIVEAWAY_TEXT.closedFooterText, where: 'The accent strip at the bottom ("STAY TUNED")' }
    ]
  },
  {
    label: 'Winners state',
    note: 'The prize line under the headline renders "<prize name> Giveaway" automatically from the prize name.',
    slots: [
      { key: 'endedHeadline', label: 'Headline', hint: DEFAULT_GIVEAWAY_TEXT.endedHeadline, where: 'The big headline ("MEET THE WINNERS")' },
      { key: 'congratsText', label: 'Congratulations line', hint: DEFAULT_GIVEAWAY_TEXT.congratsText, where: 'Under the winner boxes' },
      { key: 'endedFooterText', label: 'Strip line', hint: DEFAULT_GIVEAWAY_TEXT.endedFooterText, where: 'The accent strip at the bottom ("GIVEAWAY COMPLETE")' }
    ]
  },
  {
    label: 'All states',
    slots: [
      { key: 'rulesLabel', label: 'Rules label', hint: DEFAULT_GIVEAWAY_TEXT.rulesLabel, where: 'Before the rules link at the bottom of every state' },
      { key: 'legalText', label: 'Legal line', hint: DEFAULT_GIVEAWAY_TEXT.legalText, where: 'The small print at the bottom of every state' }
    ]
  }
]

const FontSelect = ({ label, value, hint, testid, onChange }: {
  label: string
  value: ChatFontKey
  hint: string
  testid: string
  onChange: (next: ChatFontKey) => void
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <CatalogFontSelect
      value={value}
      onChange={(next) => onChange(next as ChatFontKey)}
      defaultLabel='Default'
      data-testid={testid}
      className={inputClass}
    />
    <p className='mt-1 text-[11px] text-dm-text-accent'>{hint}</p>
  </label>
)

const isHttpsUrl = (raw: string): boolean => {
  try {
    const u = new URL(raw)
    return u.protocol === 'https:'
  } catch {
    return false
  }
}

const GiveawayForm = ({
  event,
  existing,
  onSaved,
  onUpserted,
  onCancel
}: {
  event: EventDetail
  existing: GiveawaySummary | null
  onSaved: (giveaway: GiveawaySummary) => void
  onUpserted: (giveaway: GiveawaySummary) => void
  onCancel: () => void
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [draft, setDraft] = useState<GiveawayDraft>(() =>
    existing !== null ? draftFromGiveaway(existing) : emptyDraft()
  )
  const [broadcasterUserIds, setBroadcasterUserIds] = useState<string[]>(() =>
    existing !== null ? existing.broadcasterUserIds : event.channels.map((c) => c.broadcasterUserId)
  )
  const [alternatesTracking, setAlternatesTracking] = useState<boolean>(existing === null)
  const [textOpen, setTextOpen] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [liveBusy, setLiveBusy] = useState<'start' | 'stop' | null>(null)
  const [liveError, setLiveError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(false)
  useEffect(() => {
    setSavedNote(false)
  }, [draft, broadcasterUserIds])

  const isEdit = existing !== null
  const readOnly = event.status === 'ENDED'
  const drawLocked = existing !== null && (existing.hasDraw || existing.draw != null)
  const listenLocked = existing !== null && existing.status !== 'UPCOMING' && existing.status !== 'CLOSED'
  const mechanicsDisabled = readOnly || drawLocked
  const listenDisabled = mechanicsDisabled || listenLocked

  const toggleChannel = (broadcasterUserId: string): void => {
    setBroadcasterUserIds((prev) => prev.includes(broadcasterUserId)
      ? prev.filter((id) => id !== broadcasterUserId)
      : [...prev, broadcasterUserId])
  }

  const setEntryCommand = (next: string): void => {
    setDraft((d) => ({
      ...d,
      entryCommand: next,
      openCtaText: d.openCtaText === defaultOpenCta(d.entryCommand) ? defaultOpenCta(next) : d.openCtaText
    }))
  }

  const setWinnerCount = (n: number): void => {
    setDraft((d) => ({ ...d, winnerCount: n, alternateCount: alternatesTracking ? n : d.alternateCount }))
  }

  const setText = (key: keyof GiveawayDraft & string, value: string): void => {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  const nullIfBlank = (s: string): string | null => s.trim().length > 0 ? s.trim() : null

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    const entryCommand = draft.entryCommand.trim()
    if (!entryCommand.startsWith('!') || entryCommand.length < ENTRY_COMMAND_MIN_LEN || entryCommand.length > ENTRY_COMMAND_MAX_LEN || /\s/.test(entryCommand)) {
      setError(`Entry command must start with ! and be ${ENTRY_COMMAND_MIN_LEN}–${ENTRY_COMMAND_MAX_LEN} characters with no spaces.`)
      return
    }
    if (draft.winnerCount < MIN_WINNERS || draft.winnerCount > MAX_WINNERS) {
      setError(`Number of winners must be ${MIN_WINNERS}–${MAX_WINNERS}.`)
      return
    }
    if (draft.alternateCount < MIN_ALTERNATES || draft.alternateCount > MAX_ALTERNATES) {
      setError(`Number of alternates must be ${MIN_ALTERNATES}–${MAX_ALTERNATES}.`)
      return
    }
    if (!isHttpsUrl(draft.rulesUrl.trim())) {
      setError('Rules link must be an https:// URL it renders on the overlay in every state.')
      return
    }
    if (draft.sponsorName.trim().length === 0) {
      setError('Sponsor / promoter name is required (the Twitch disclaimer names it).')
      return
    }
    if (draft.prizeName.trim().length === 0) {
      setError('Prize name is required.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const sharedVars = {
        name: draft.name.trim().length > 0 ? draft.name.trim() : draft.prizeName.trim(),
        broadcasterUserIds: listenDisabled ? null : broadcasterUserIds,
        entryCommand: listenDisabled ? null : entryCommand,
        winnerCount: mechanicsDisabled ? null : draft.winnerCount,
        alternateCount: mechanicsDisabled ? null : draft.alternateCount,
        excludeBroadcaster: mechanicsDisabled ? null : draft.excludeBroadcaster,
        excludeModerators: mechanicsDisabled ? null : draft.excludeModerators,
        rulesUrl: draft.rulesUrl.trim(),
        sponsorName: draft.sponsorName.trim(),
        prizeName: draft.prizeName.trim(),
        prizeImageUrl: nullIfBlank(draft.prizeImageUrl),
        brandLogoUrl: nullIfBlank(draft.brandLogoUrl),
        brandText: nullIfBlank(draft.brandText),
        backgroundImage: nullIfBlank(draft.backgroundImage),
        backgroundImageFit: draft.backgroundImage.trim().length > 0 ? draft.backgroundImageFit : null,
        titleText: nullIfBlank(draft.titleText),
        openCtaText: nullIfBlank(draft.openCtaText) ?? defaultOpenCta(entryCommand),
        entrantsLabel: nullIfBlank(draft.entrantsLabel),
        openFooterText: nullIfBlank(draft.openFooterText),
        upcomingHeadline: nullIfBlank(draft.upcomingHeadline),
        upcomingCtaText: nullIfBlank(draft.upcomingCtaText),
        closedHeadline: nullIfBlank(draft.closedHeadline),
        closedSubhead: nullIfBlank(draft.closedSubhead),
        closedBody: nullIfBlank(draft.closedBody),
        closedFooterText: nullIfBlank(draft.closedFooterText),
        endedHeadline: nullIfBlank(draft.endedHeadline),
        congratsText: nullIfBlank(draft.congratsText),
        endedFooterText: nullIfBlank(draft.endedFooterText),
        rulesLabel: nullIfBlank(draft.rulesLabel),
        legalText: draft.legalText.trim().length === 0
          ? ''
          : draft.legalText.trim() === DEFAULT_GIVEAWAY_TEXT.legalText ? null : draft.legalText.trim(),
        titleFontKey: draft.titleFontKey,
        textFontKey: draft.textFontKey,
        captionFontKey: draft.captionFontKey,
        subtitleFontKey: draft.subtitleFontKey,
        textColor: draft.textColor,
        background: draft.background,
        borderColor: draft.borderColor,
        borderWidth: draft.borderWidth,
        cornerRadius: draft.cornerRadius,
        winnerOutlineColor: draft.winnerOutlineColor,
        accentBackground: draft.accentBackground,
        accentTextColor: draft.accentTextColor,
        prizeImageRadius: draft.prizeImageRadius,
        prizeImageBorderWidth: draft.prizeImageBorderWidth,
        prizeImageBorderColor: draft.prizeImageBorderColor,
        prizeImageWidthPx: draft.prizeImageWidthPx,
        countDividerEnabled: draft.countDividerEnabled,
        countDividerColor: draft.countDividerColor,
        countDividerWidthPx: draft.countDividerWidthPx,
        brandDividerEnabled: draft.brandDividerEnabled,
        brandDividerColor: draft.brandDividerColor,
        brandDividerWidthPx: draft.brandDividerWidthPx
      }
      if (isEdit) {
        const { giveaway } = await graphqlRequest<{ giveaway: GiveawaySummary }>(
          updateGiveawayMutation,
          { giveawayId: existing.giveawayId, ...sharedVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(giveaway)
      } else {
        const { giveaway } = await graphqlRequest<{ giveaway: GiveawaySummary }>(
          createGiveawayMutation,
          { eventId: event.eventId, ...sharedVars },
          auth0Token
        )
        setSavedNote(true)
        onSaved(giveaway)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save giveaway')
    } finally {
      setSubmitting(false)
    }
  }

  type LiveResponse = Pick<GiveawaySummary, 'giveawayId' | 'status' | 'entrantCount' | 'hasDraw' | 'startedAt' | 'closedAt' | 'publishedAt' | 'version' | 'winners'>
  const handleStart = async (): Promise<void> => {
    if (auth0Token === undefined || existing === null || liveBusy !== null || readOnly) return
    setLiveBusy('start')
    setLiveError(null)
    try {
      const { giveaway } = await graphqlRequest<{ giveaway: LiveResponse }>(
        startGiveawayMutation, { giveawayId: existing.giveawayId }, auth0Token
      )
      onUpserted({ ...existing, ...giveaway })
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to open the giveaway')
    } finally {
      setLiveBusy(null)
    }
  }

  const handleStop = async (): Promise<void> => {
    if (auth0Token === undefined || existing === null || liveBusy !== null) return
    setLiveBusy('stop')
    setLiveError(null)
    try {
      const { giveaway } = await graphqlRequest<{ giveaway: LiveResponse }>(
        stopGiveawayMutation, { giveawayId: existing.giveawayId }, auth0Token
      )
      onUpserted({ ...existing, ...giveaway })
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to close the giveaway')
    } finally {
      setLiveBusy(null)
    }
  }

  const canStart = existing !== null && !drawLocked && (existing.status === 'UPCOMING' || existing.status === 'CLOSED')
  const canStop = existing !== null && existing.status === 'OPEN'

  return (
    <form
      onSubmit={(e) => { void handleSubmit(e) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault()
        }
      }}
      className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3'
    >
      <div className='space-y-1.5 lg:col-span-2'>
        <fieldset disabled={readOnly} className='contents space-y-1.5'>
          <SectionCard
            title={readOnly ? 'View giveaway' : isEdit ? 'Edit giveaway' : 'New giveaway'}
            action={
            readOnly
              ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
                  <EyeIcon className='size-3' aria-hidden='true' />
                  Read-only
                </span>
                )
              : (
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to giveaways
                </button>
                )
          }
          >
            <label className='mb-4 block'>
              <FieldLabel>Name</FieldLabel>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder='Short label for this giveaway (shown in the list)'
                data-testid='giveaway-name-input'
                className={inputClass}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                Displayed in the giveaways list. Defaults to the prize name when omitted.
              </p>
            </label>

            {drawLocked && (
              <div className='mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200' data-testid='giveaway-mechanics-locked'>
                A draw record exists: the entry command, channels, winner / alternate counts and
                exclusions are locked so the draw matches its record. Cosmetics stay editable.
                Create another giveaway for the next prize.
              </div>
            )}
            {!drawLocked && listenLocked && (
              <div className='mb-4 rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-[11px] leading-relaxed text-sky-200' data-testid='giveaway-listen-locked'>
                Entries are open: the entry command and channels are locked while viewers can enter.
                Stop the giveaway to change them. Everything else stays editable.
              </div>
            )}

            <div className='mb-4 grid grid-cols-1 gap-3 md:grid-cols-3'>
              <label className='block'>
                <FieldLabel>Entry command</FieldLabel>
                <input
                  value={draft.entryCommand}
                  onChange={(e) => setEntryCommand(e.target.value)}
                  placeholder={DEFAULT_ENTRY_COMMAND}
                  required
                  maxLength={ENTRY_COMMAND_MAX_LEN}
                  disabled={listenDisabled}
                  data-testid='giveaway-entry-command-input'
                  className={classNames(inputClass, 'font-mono disabled:opacity-60')}
                />
              </label>
              <label className='block'>
                <FieldLabel>Winners</FieldLabel>
                <NumberInput
                  value={draft.winnerCount}
                  min={MIN_WINNERS}
                  max={MAX_WINNERS}
                  onCommit={setWinnerCount}
                  disabled={mechanicsDisabled}
                  data-testid='giveaway-winner-count-input'
                  className={classNames(inputClass, 'disabled:opacity-60')}
                />
              </label>
              <label className='block'>
                <FieldLabel>Alternates</FieldLabel>
                <NumberInput
                  value={draft.alternateCount}
                  min={MIN_ALTERNATES}
                  max={MAX_ALTERNATES}
                  onCommit={(n) => { setAlternatesTracking(false); setDraft((d) => ({ ...d, alternateCount: n })) }}
                  disabled={mechanicsDisabled}
                  data-testid='giveaway-alternate-count-input'
                  className={classNames(inputClass, 'disabled:opacity-60')}
                />
              </label>
            </div>
            <p className='-mt-2 mb-4 text-[11px] text-dm-text-accent'>
              Viewers type the command anywhere in a chat message (case-insensitive) one entry per
              Twitch account across every listened channel, no re-entry. Alternates are drawn in the
              same verifiable draw, in order, and never appear on the overlay.
            </p>

            <div className='mb-4'>
              <FieldLabel>
                <span className='flex items-center gap-1.5'>
                  <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                  Twitch channels
                </span>
              </FieldLabel>
              {event.channels.length === 0
                ? (
                  <p className='rounded-md border border-dashed border-white/10 p-3 text-xs text-dm-text-secondary'>
                    No channels bound to this stream yet. Add one on the Integrations page.
                  </p>
                  )
                : (
                  <ul className='space-y-1.5'>
                    {event.channels.map((c: ChannelBinding) => (
                      <li key={c.broadcasterUserId}>
                        <label className={classNames('flex items-center gap-2.5 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-sm', listenDisabled ? 'opacity-60' : 'cursor-pointer hover:bg-dm-bg-accent/50')}>
                          <input
                            type='checkbox'
                            checked={broadcasterUserIds.includes(c.broadcasterUserId)}
                            onChange={() => toggleChannel(c.broadcasterUserId)}
                            disabled={listenDisabled}
                            className={checkboxClass}
                          />
                          <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
                          <span className='text-dm-text-primary'>{c.channelLogin}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  )}
            </div>

            <div className='mb-4 space-y-2'>
              <FieldLabel>Eligibility exclusions</FieldLabel>
              <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={draft.excludeBroadcaster}
                  onChange={(e) => setDraft((d) => ({ ...d, excludeBroadcaster: e.target.checked }))}
                  disabled={mechanicsDisabled}
                  data-testid='giveaway-exclude-broadcaster'
                  className={checkboxClass}
                />
                Exclude the broadcaster account(s)
              </label>
              <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
                <input
                  type='checkbox'
                  checked={draft.excludeModerators}
                  onChange={(e) => setDraft((d) => ({ ...d, excludeModerators: e.target.checked }))}
                  disabled={mechanicsDisabled}
                  data-testid='giveaway-exclude-moderators'
                  className={checkboxClass}
                />
                Exclude channel moderators
              </label>
              <p className='text-[11px] leading-relaxed text-dm-text-accent'>
                Applied at draw time from the badges each entrant carried when they entered. Excluded
                entrants still appear in the CSV as excluded, with the reason. The environment bot
                is always excluded.
              </p>
            </div>

            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <label className='block'>
                <FieldLabel>Official rules link</FieldLabel>
                <input
                  value={draft.rulesUrl}
                  onChange={(e) => setDraft((d) => ({ ...d, rulesUrl: e.target.value }))}
                  placeholder='https://your.site/giveaway-rules'
                  required
                  inputMode='url'
                  data-testid='giveaway-rules-url-input'
                  className={inputClass}
                />
                <p className='mt-1 text-[11px] text-dm-text-accent'>
                  Shown as text in every state — keep it short. Must contain the sponsor&apos;s name and
                  address, entry period, eligibility, odds, draw method and the Twitch disclaimer.
                </p>
              </label>
              <label className='block'>
                <FieldLabel>Sponsor / promoter name</FieldLabel>
                <input
                  value={draft.sponsorName}
                  onChange={(e) => setDraft((d) => ({ ...d, sponsorName: e.target.value }))}
                  placeholder='Your legal or channel name'
                  required
                  data-testid='giveaway-sponsor-name-input'
                  className={inputClass}
                />
                <p className='mt-1 text-[11px] text-dm-text-accent'>
                  Fills the &quot;This is a promotion by …&quot; line Twitch requires on screen.
                </p>
              </label>
            </div>
          </SectionCard>
          <SectionCard title='Prize & brand'>
            <label className='mb-4 block'>
              <FieldLabel>Prize name</FieldLabel>
              <input
                value={draft.prizeName}
                onChange={(e) => setDraft((d) => ({ ...d, prizeName: e.target.value }))}
                placeholder='StagePilot prize'
                required
                data-testid='giveaway-prize-name-input'
                className={inputClass}
              />
              <p className='mt-1 text-[11px] text-dm-text-accent'>
                The prize line on the winners card. A brand-supplied prize is Branded Content
                turn on Twitch&apos;s branded-content toggle and say &quot;Sponsored by …&quot; on stream.
              </p>
            </label>
            <div className='mb-4'>
              <ImageUpload
                value={draft.prizeImageUrl}
                onChange={(v) => setDraft((d) => ({ ...d, prizeImageUrl: v }))}
                shape='square'
                label='Prize photo (optional)'
                helpText='Square reads best shown left of the copy on the upcoming banner and the open card.'
              />
            </div>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <ImageUpload
                value={draft.brandLogoUrl}
                onChange={(v) => setDraft((d) => ({ ...d, brandLogoUrl: v }))}
                shape='square'
                label='Brand logo (optional)'
                helpText='Wins over the brand text when set. Transparent PNG, wide, ~48 px tall on screen.'
              />
              <label className='block'>
                <FieldLabel>Brand text (when no logo)</FieldLabel>
                <input
                  value={draft.brandText}
                  onChange={(e) => setDraft((d) => ({ ...d, brandText: e.target.value }))}
                  placeholder='STAGEPILOT'
                  data-testid='giveaway-brand-text-input'
                  className={inputClass}
                />
                <p className='mt-1 text-[11px] text-dm-text-accent'>
                  Rendered uppercase, letter-spaced, where the logo would sit. Leave both empty for
                  no brand row.
                </p>
              </label>
            </div>
            <div className='mt-4'>
              <BackgroundImageInput
                url={draft.backgroundImage}
                fit={draft.backgroundImageFit}
                onChangeUrl={(v) => setDraft((d) => ({ ...d, backgroundImage: v }))}
                onChangeFit={(v) => setDraft((d) => ({ ...d, backgroundImageFit: v }))}
                label='Overlay background image (optional)'
              />
            </div>
          </SectionCard>
          <SectionCard
            title='Text'
            action={
              <button
                type='button'
                onClick={() => setTextOpen((o) => !o)}
                aria-expanded={textOpen}
                data-testid='giveaway-text-toggle'
                className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
              >
                {textOpen
                  ? <ChevronDownIcon className='size-3.5' aria-hidden='true' />
                  : <ChevronRightIcon className='size-3.5' aria-hidden='true' />}
                {textOpen ? 'Hide text slots' : 'Edit text slots'}
              </button>
            }
          >
            {textOpen
              ? (
                <div className='space-y-5'>
                  {TEXT_GROUPS.map((group) => (
                    <div key={group.label} data-testid={`giveaway-text-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className='mb-2 border-b border-white/5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                        {group.label}
                      </div>
                      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                        {group.slots.map((slot) => (
                          <label key={slot.key} className={classNames('block', slot.key === 'legalText' && 'md:col-span-2')}>
                            {slot.key === 'legalText'
                              ? (
                                <>
                                  <span className='mb-1.5 flex items-center justify-between'>
                                    <span className='block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>{slot.label}</span>
                                    <button
                                      type='button'
                                      onClick={() => setText('legalText', DEFAULT_GIVEAWAY_TEXT.legalText)}
                                      data-testid='giveaway-legal-restore'
                                      className='rounded-md border border-white/10 px-2 py-0.5 text-[10px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                                    >
                                      Restore default copy
                                    </button>
                                  </span>
                                  <textarea
                                    value={draft.legalText}
                                    onChange={(e) => setText('legalText', e.target.value)}
                                    placeholder='(empty the legal line is hidden)'
                                    rows={3}
                                    data-testid='giveaway-text-legalText'
                                    className={inputClass}
                                  />
                                  <p className='mt-1 text-[11px] text-dm-text-accent'>
                                    {slot.where}. <code>{'{sponsor}'}</code> is replaced with the sponsor
                                    name keep the Twitch disclaimer in it (Twitch requires it displayed
                                    or read out). <strong>Leave empty to hide this line.</strong>
                                  </p>
                                </>
                                )
                              : (
                                <>
                                  <FieldLabel>{slot.label}</FieldLabel>
                                  <input
                                    value={draft[slot.key] as string}
                                    onChange={(e) => setText(slot.key, e.target.value)}
                                    placeholder={slot.hint}
                                    data-testid={`giveaway-text-${slot.key}`}
                                    className={inputClass}
                                  />
                                  <p className='mt-1 text-[11px] text-dm-text-accent'>{slot.where}</p>
                                </>
                                )}
                          </label>
                        ))}
                      </div>
                      {group.note !== undefined && (
                        <p className='mt-2 text-[11px] leading-relaxed text-dm-text-accent'>{group.note}</p>
                      )}
                    </div>
                  ))}
                  <p className='text-[11px] leading-relaxed text-dm-text-accent'>
                    Leave a slot empty to use the default copy shown as its placeholder except the
                    legal line, where empty hides the line entirely.
                  </p>
                </div>
                )
              : (
                <p className='text-[11px] text-dm-text-accent'>
                  Every headline, strip, label and the legal line is editable, grouped by the overlay
                  state it appears in.
                </p>
                )}
          </SectionCard>
          <SectionCard title='Style'>
            <div className='mb-4 grid grid-cols-1 gap-3 md:grid-cols-2'>
              <FontSelect
                label='Titles font'
                value={draft.titleFontKey}
                testid='giveaway-title-font-select'
                hint='The big headlines in every state, the title line, the live entrant count and the winner usernames.'
                onChange={(v) => setDraft((d) => ({ ...d, titleFontKey: v }))}
              />
              <FontSelect
                label='Subtitle font'
                value={draft.subtitleFontKey}
                testid='giveaway-subtitle-font-select'
                hint='The text in the black accent bars: the open / stay-tuned / complete strip lines and the upcoming banner strip.'
                onChange={(v) => setDraft((d) => ({ ...d, subtitleFontKey: v }))}
              />
              <FontSelect
                label='Text font'
                value={draft.textFontKey}
                testid='giveaway-text-font-select'
                hint='The call to action, subheads, the prize line, the entrants label, rules and legal line.'
                onChange={(v) => setDraft((d) => ({ ...d, textFontKey: v }))}
              />
              <FontSelect
                label='Caption font'
                value={draft.captionFontKey}
                testid='giveaway-caption-font-select'
                hint='The closed-state body line and the congratulations line.'
                onChange={(v) => setDraft((d) => ({ ...d, captionFontKey: v }))}
              />
            </div>
            <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
              <ColorInput
                label='Text color'
                value={draft.textColor}
                onChange={(v) => setDraft((d) => ({ ...d, textColor: v }))}
              />
              <BackgroundInput
                label='Card background'
                value={draft.background}
                onChange={(v) => setDraft((d) => ({ ...d, background: v }))}
              />
              <BackgroundInput
                label='Accent strip / winner box background'
                value={draft.accentBackground}
                onChange={(v) => setDraft((d) => ({ ...d, accentBackground: v }))}
              />
              <ColorInput
                label='Accent text color'
                value={draft.accentTextColor}
                onChange={(v) => setDraft((d) => ({ ...d, accentTextColor: v }))}
              />
            </div>
            <div className='mt-4 grid grid-cols-1 gap-3 md:grid-cols-2'>
              <label className='block'>
                <FieldLabel>Corner radius (px)</FieldLabel>
                <div className='flex items-center gap-2'>
                  <input
                    type='range'
                    min={MIN_CORNER_RADIUS}
                    max={MAX_CORNER_RADIUS}
                    value={draft.cornerRadius}
                    onChange={(e) => setDraft((d) => ({ ...d, cornerRadius: Number.parseInt(e.target.value, 10) }))}
                    className='flex-1 accent-brand'
                  />
                  <NumberInput
                    value={draft.cornerRadius}
                    min={MIN_CORNER_RADIUS}
                    max={MAX_CORNER_RADIUS}
                    onCommit={(n) => setDraft((d) => ({ ...d, cornerRadius: n }))}
                    data-testid='giveaway-corner-radius-input'
                    className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                </div>
              </label>
              <label className='block'>
                <FieldLabel>Border width (px)</FieldLabel>
                <NumberInput
                  value={draft.borderWidth}
                  min={MIN_BORDER_WIDTH}
                  max={MAX_BORDER_WIDTH}
                  onCommit={(n) => setDraft((d) => ({ ...d, borderWidth: n }))}
                  data-testid='giveaway-border-width-input'
                  className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                />
              </label>
              <div>
                <ColorInput
                  label='Border color'
                  value={draft.borderColor}
                  onChange={(v) => setDraft((d) => ({ ...d, borderColor: v }))}
                />
                <p className='mt-1 text-[11px] text-dm-text-accent'>
                  Set the width to 0, or the opacity to 0, for no border.
                </p>
              </div>
              <div>
                <ColorInput
                  label='Winner outline color'
                  value={draft.winnerOutlineColor}
                  onChange={(v) => setDraft((d) => ({ ...d, winnerOutlineColor: v }))}
                />
                <p className='mt-1 text-[11px] text-dm-text-accent'>
                  The glowing ring + white glint around each winner box once winners are published.
                </p>
              </div>
            </div>
            <div className='mt-5' data-testid='giveaway-style-image-group'>
              <div className='mb-2 border-b border-white/5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                Image
              </div>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <label className='block'>
                  <FieldLabel>Prize photo size (px)</FieldLabel>
                  <NumberInput
                    value={draft.prizeImageWidthPx}
                    min={MIN_PRIZE_IMAGE_WIDTH}
                    max={MAX_PRIZE_IMAGE_WIDTH}
                    onCommit={(n) => setDraft((d) => ({ ...d, prizeImageWidthPx: n }))}
                    data-testid='giveaway-prize-image-width-input'
                    className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                  <p className='mt-1 text-[11px] text-dm-text-accent'>
                    Square, rendered at this size on the open card; the upcoming banner scales it
                    down proportionally.
                  </p>
                </label>
                <label className='block'>
                  <FieldLabel>Photo corner radius (px)</FieldLabel>
                  <div className='flex items-center gap-2'>
                    <input
                      type='range'
                      min={MIN_PRIZE_IMAGE_RADIUS}
                      max={MAX_PRIZE_IMAGE_RADIUS}
                      value={draft.prizeImageRadius}
                      onChange={(e) => setDraft((d) => ({ ...d, prizeImageRadius: Number.parseInt(e.target.value, 10) }))}
                      className='flex-1 accent-brand'
                    />
                    <NumberInput
                      value={draft.prizeImageRadius}
                      min={MIN_PRIZE_IMAGE_RADIUS}
                      max={MAX_PRIZE_IMAGE_RADIUS}
                      onCommit={(n) => setDraft((d) => ({ ...d, prizeImageRadius: n }))}
                      data-testid='giveaway-prize-image-radius-input'
                      className='w-20 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                    />
                  </div>
                </label>
                <label className='block'>
                  <FieldLabel>Photo border width (px)</FieldLabel>
                  <NumberInput
                    value={draft.prizeImageBorderWidth}
                    min={MIN_PRIZE_IMAGE_BORDER_WIDTH}
                    max={MAX_PRIZE_IMAGE_BORDER_WIDTH}
                    onCommit={(n) => setDraft((d) => ({ ...d, prizeImageBorderWidth: n }))}
                    data-testid='giveaway-prize-image-border-width-input'
                    className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                </label>
                <ColorInput
                  label='Photo border color'
                  value={draft.prizeImageBorderColor}
                  onChange={(v) => setDraft((d) => ({ ...d, prizeImageBorderColor: v }))}
                />
              </div>
            </div>
            <div className='mt-5' data-testid='giveaway-style-dividers-group'>
              <div className='mb-2 border-b border-white/5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
                Dividers
              </div>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <div>
                  <label className='block'>
                    <FieldLabel>Brand divider</FieldLabel>
                    <select
                      value={draft.brandDividerEnabled === null ? 'legacy' : draft.brandDividerEnabled ? 'on' : 'off'}
                      onChange={(e) => setDraft((d) => ({ ...d, brandDividerEnabled: e.target.value === 'legacy' ? null : e.target.value === 'on' }))}
                      data-testid='giveaway-brand-divider-mode'
                      className={inputClass}
                    >
                      <option value='legacy'>Original look (upcoming banner only)</option>
                      <option value='on'>On — under the brand in every state</option>
                      <option value='off'>Off</option>
                    </select>
                    <p className='mt-1 text-[11px] text-dm-text-accent'>
                      The horizontal rule under the brand name / logo.
                    </p>
                  </label>
                  {draft.brandDividerEnabled === true && (
                    <div className='mt-3 space-y-3'>
                      <ColorInput
                        label='Brand divider color'
                        value={draft.brandDividerColor}
                        onChange={(v) => setDraft((d) => ({ ...d, brandDividerColor: v }))}
                      />
                      <label className='block'>
                        <FieldLabel>Brand divider thickness (px)</FieldLabel>
                        <NumberInput
                          value={draft.brandDividerWidthPx}
                          min={MIN_DIVIDER_WIDTH}
                          max={MAX_DIVIDER_WIDTH}
                          onCommit={(n) => setDraft((d) => ({ ...d, brandDividerWidthPx: n }))}
                          data-testid='giveaway-brand-divider-width-input'
                          className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                        />
                      </label>
                    </div>
                  )}
                </div>
                <div>
                  <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
                    <input
                      type='checkbox'
                      checked={draft.countDividerEnabled}
                      onChange={(e) => setDraft((d) => ({ ...d, countDividerEnabled: e.target.checked }))}
                      data-testid='giveaway-count-divider-toggle'
                      className={checkboxClass}
                    />
                    Divider between the prize photo and the count
                  </label>
                  <p className='mt-1 text-[11px] text-dm-text-accent'>
                    Open state only; shows when a prize photo is set.
                  </p>
                  {draft.countDividerEnabled && (
                    <div className='mt-3 space-y-3'>
                      <ColorInput
                        label='Count divider color'
                        value={draft.countDividerColor}
                        onChange={(v) => setDraft((d) => ({ ...d, countDividerColor: v }))}
                      />
                      <label className='block'>
                        <FieldLabel>Count divider thickness (px)</FieldLabel>
                        <NumberInput
                          value={draft.countDividerWidthPx}
                          min={MIN_DIVIDER_WIDTH}
                          max={MAX_DIVIDER_WIDTH}
                          onCommit={(n) => setDraft((d) => ({ ...d, countDividerWidthPx: n }))}
                          data-testid='giveaway-count-divider-width-input'
                          className='w-24 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>
        </fieldset>
        {existing?.hasDraw === true && existing.draw != null && (
          <DrawSection giveaway={existing} draw={existing.draw} readOnly={readOnly} onUpserted={onUpserted} />
        )}
      </div>
      <div className='lg:col-span-1'>
        <div className='sticky top-4 space-y-3'>
          {readOnly
            ? (
              <>
                <div className='rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary'>
                  This stream has ended, so its giveaways are read-only. The preview below shows
                  the giveaway&apos;s last state.
                </div>
                <button
                  type='button'
                  onClick={onCancel}
                  className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
                >
                  <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
                  Back to giveaways
                </button>
              </>
              )
            : (
              <div className='flex items-center gap-2'>
                <button
                  type='submit'
                  disabled={submitting}
                  data-testid='giveaway-save-button'
                  className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {submitting && <LoadingSpinner />}
                  {isEdit ? 'Save changes' : 'Create giveaway'}
                </button>
                <button
                  type='button'
                  onClick={onCancel}
                  disabled={submitting}
                  className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
              )}
          {error !== null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='giveaway-saved' />}
          {isEdit && existing !== null && (
            <div className='rounded-xl border border-white/5 bg-dm-bg-secondary p-4'>
              <div className='mb-3 flex items-center justify-between'>
                <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>Live control</h4>
                <span
                  data-testid='giveaway-form-status'
                  className={classNames(
                    'rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                    GIVEAWAY_STATUS_PILL[existing.status]
                  )}
                >
                  {GIVEAWAY_STATUS_LABEL[existing.status]}
                </span>
              </div>
              <div className='mb-3 text-xs text-dm-text-secondary'>
                <span className='font-mono tabular-nums text-dm-text-primary' data-testid='giveaway-form-entrant-count'>{existing.entrantCount.toLocaleString()}</span> entrants
                {existing.hasDraw && existing.status !== 'ENDED' && (
                  <span className='ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300 ring-1 ring-inset ring-amber-500/30'>winners drawn (unpublished)</span>
                )}
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <button
                  type='button'
                  onClick={() => { void handleStart() }}
                  disabled={liveBusy !== null || !canStart || readOnly}
                  data-testid='giveaway-form-start'
                  className='inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {liveBusy === 'start' ? <LoadingSpinner /> : <PlayCircleIcon className='size-4' aria-hidden='true' />}
                  Start
                </button>
                <button
                  type='button'
                  onClick={() => { void handleStop() }}
                  disabled={liveBusy !== null || !canStop}
                  data-testid='giveaway-form-stop'
                  className='inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {liveBusy === 'stop' ? <LoadingSpinner /> : <StopCircleIcon className='size-4' aria-hidden='true' />}
                  Stop
                </button>
              </div>
              {liveError !== null && (
                <div className='mt-2'>
                  <ErrorInline onDismiss={() => setLiveError(null)}>{liveError}</ErrorInline>
                </div>
              )}
              <p className='mt-2 text-[11px] leading-relaxed text-dm-text-accent'>
                Start opens entries (Upcoming → Open); Stop closes them. Calculate winners, Publish
                winners and Reset live in the overlay editor&apos;s Selected Element Controls.
              </p>
            </div>
          )}

          <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
            Preview
          </h4>
          <GiveawayPreviewBody draft={draft} existing={existing} readOnly={readOnly} />
          <p className='text-[11px] text-dm-text-accent'>
            {readOnly
              ? 'Final overlay rendering applies the chosen fonts and dividers exactly as previewed.'
              : 'The overlay shows ONE of these at a time, by state. The open count and the winner handles here are samples.'}
          </p>
        </div>
      </div>
    </form>
  )
}

const GiveawayListRow = ({
  giveaway,
  onEdit,
  onDelete,
  busy,
  readOnly
}: {
  giveaway: GiveawaySummary
  onEdit: () => void
  onDelete: () => void
  busy: boolean
  readOnly: boolean
}): JSX.Element => {
  const isOpen = giveaway.status === 'OPEN'
  return (
    <li
      data-testid={`giveaway-row-${giveaway.giveawayId}`}
      className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className={classNames(
            'shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
            GIVEAWAY_STATUS_PILL[giveaway.status]
          )}
          >
            {GIVEAWAY_STATUS_LABEL[giveaway.status]}
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>
              {giveaway.name}
            </span>
            <span className='truncate text-[11px] text-dm-text-accent'>
              {giveaway.prizeName} · <span className='font-mono'>{giveaway.entryCommand}</span> · {giveaway.entrantCount.toLocaleString()} entrants ·{' '}
              {giveaway.winnerCount} winner{giveaway.winnerCount === 1 ? '' : 's'}
              {giveaway.hasDraw ? ' · drawn' : ''}
              {giveaway.broadcasterUserIds.length > 1 ? ` · ${giveaway.broadcasterUserIds.length} channels` : ''}
            </span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <button
            type='button'
            onClick={onEdit}
            title={readOnly ? 'View' : 'Edit'}
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {readOnly
              ? <EyeIcon className='size-3.5' aria-hidden='true' />
              : <PencilSquareIcon className='size-3.5' aria-hidden='true' />}
            {readOnly ? 'View' : 'Edit'}
          </button>
          {!readOnly && (
            <button
              type='button'
              onClick={onDelete}
              disabled={busy || isOpen}
              title={isOpen ? 'Stop this giveaway first (on its form or in the overlay editor)' : 'Delete'}
              data-testid={`giveaway-delete-${giveaway.giveawayId}`}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {busy ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
              Delete
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

type Mode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit', giveawayId: string }

export const GiveawaysPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const { staffUncapped } = useEntitlements()
  const { event, setEvent, refetchEvent } = useEventDetail()
  const isEnded = event.status === 'ENDED'
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [rowBusyId, setRowBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const editing = useMemo<GiveawaySummary | null>(() => {
    if (mode.kind !== 'edit') return null
    return event.giveaways.find((g) => g.giveawayId === mode.giveawayId) ?? null
  }, [mode, event.giveaways])

  const upsertGiveaway = (giveaway: GiveawaySummary): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          giveaways: prev.giveaways.some((g) => g.giveawayId === giveaway.giveawayId)
            ? prev.giveaways.map((g) => g.giveawayId === giveaway.giveawayId ? giveaway : g)
            : [...prev.giveaways, giveaway]
        }))
  }

  const removeGiveaway = (giveawayId: string): void => {
    setEvent((prev) => prev === null
      ? prev
      : ({
          ...prev,
          giveaways: prev.giveaways.filter((g) => g.giveawayId !== giveawayId)
        }))
  }

  const handleDelete = async (giveaway: GiveawaySummary): Promise<void> => {
    if (auth0Token === undefined || rowBusyId !== null) return
    const ok = globalThis.confirm(
      giveaway.hasDraw || giveaway.entrantCount > 0
        ? `Delete giveaway "${giveaway.name}"?\n\nWARNING: it holds ${giveaway.entrantCount.toLocaleString()} entrant record${giveaway.entrantCount === 1 ? '' : 's'}${giveaway.hasDraw ? ' and a completed draw record' : ''}. Deleting destroys those records — export the CSV first if you need them for your files. This cannot be undone.`
        : `Delete giveaway "${giveaway.name}"? This cannot be undone.`
    )
    if (!ok) return
    setRowBusyId(giveaway.giveawayId)
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteGiveawayMutation, { giveawayId: giveaway.giveawayId }, auth0Token
      )
      if (result.deleted) removeGiveaway(giveaway.giveawayId)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete giveaway')
    } finally {
      setRowBusyId(null)
    }
  }

  if (!staffUncapped) {
    return <Navigate to={eventSectionPath(event.eventId, 'overview')} replace />
  }

  if (mode.kind === 'create') {
    return (
      <GiveawayForm
        event={event}
        existing={null}
        onSaved={(giveaway) => { upsertGiveaway(giveaway); setMode({ kind: 'edit', giveawayId: giveaway.giveawayId }) }}
        onUpserted={upsertGiveaway}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  if (mode.kind === 'edit') {
    if (editing === null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>That giveaway no longer exists. Returning to the list…</ErrorInline>
          <button onClick={() => setMode({ kind: 'list' })} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Back to giveaways
          </button>
        </div>
      )
    }
    return (
      <GiveawayForm
        event={event}
        existing={editing}
        onSaved={upsertGiveaway}
        onUpserted={upsertGiveaway}
        onCancel={() => setMode({ kind: 'list' })}
      />
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title='Giveaways'
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void refetchEvent() }}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowPathIcon className='size-3.5' aria-hidden='true' />
              Refresh
            </button>
            {!isEnded && (
              <button
                type='button'
                data-testid='add-giveaway-button'
                onClick={() => setMode({ kind: 'create' })}
                className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
              >
                <PlusIcon className='size-3.5' aria-hidden='true' />
                Add giveaway
              </button>
            )}
          </div>
        }
      >
        {rowError !== null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}

        {event.giveaways.length === 0
          ? (
            <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary'>
              <GiftIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
              <p>{isEnded ? 'This stream ended with no giveaways.' : 'No giveaways yet for this stream.'}</p>
              {!isEnded && (
                <button
                  type='button'
                  onClick={() => setMode({ kind: 'create' })}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80'
                >
                  <PlusIcon className='size-3.5' aria-hidden='true' />
                  Create the first one
                </button>
              )}
            </div>
            )
          : (
            <ul className='space-y-2' data-testid='giveaways-list'>
              {sortByName(event.giveaways, (g) => g.name, (g) => g.giveawayId).map((giveaway: GiveawaySummary) => (
                <GiveawayListRow
                  key={giveaway.giveawayId}
                  giveaway={giveaway}
                  onEdit={() => setMode({ kind: 'edit', giveawayId: giveaway.giveawayId })}
                  onDelete={() => { void handleDelete(giveaway) }}
                  busy={rowBusyId === giveaway.giveawayId}
                  readOnly={isEnded}
                />
              ))}
            </ul>
            )}
      </SectionCard>
    </div>
  )
}
